/**
 * CheckboxField component
 */

// Library
import { useId, useMemo, useState } from 'react';
import { Text } from '@components';
import { Checkbox as CheckboxIcon, WarningQld } from '@public';
import { Checkbox } from 'react-aria-components';

// CSS Style for this module
import styles from './checkboxField.module.scss';

/**
 * Interface for CheckboxField props
 */
interface CheckboxFieldProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * The value of the checkbox input
	 */
	value?: boolean;
	/**
	 * onChange function for keeping track field value
	 */
	onChange?: (checked: boolean) => void;
	/**
	 * label text
	 */
	label?: string;
	/**
	 * Helper text for additional guidance about how the input field will be used.
	 * It should only take up a single line
	 */
	hintText?: string;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Boolean for determining whether this field is required
	 */
	required?: boolean;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
	/**
	 * Label content instead of label text
	 */
	children?: React.ReactNode;
	/**
	 * Boolean for determining whether this field is indeterminate
	 */
	indeterminate?: boolean;
}

/**
 * Checkbox field
 * @param {CheckboxFieldProps} Prop
 * @returns an styled checkbox input element
 */
export const CheckboxField = ({
	customStyles,
	value,
	label,
	onChange,
	hintText,
	errorMsg,
	disabled = false,
	required = false,
	autoFocus = false,
	children,
	indeterminate = false,
}: CheckboxFieldProps) => {
	const internalId = useId();
	const [hintId] = useState(`${internalId}-hint`);
	const [errorId] = useState(`${internalId}-error`);
	const hasError = useMemo(() => Boolean(errorMsg), [errorMsg]);
	return (
		<div className={[styles.container, customStyles].join(' ')}>
			<div className={styles.textContainer}>
				{hintText && (
					<div>
						<Text variant="muted" id={hintId}>
							{hintText}
						</Text>
					</div>
				)}
				{hasError && (
					<div className={styles.errorContainer}>
						<Text variant="error" id={errorId} role="alert" className={styles.errorContent}>
							<WarningQld customStyles={styles.errorIcon} /> {errorMsg}
						</Text>
					</div>
				)}
			</div>
			<Checkbox
				className={styles.checkboxContainer}
				isSelected={value}
				onChange={onChange}
				isDisabled={disabled}
				isRequired={required}
				autoFocus={autoFocus}
				isIndeterminate={indeterminate}
				id={internalId}
			>
				{({ isIndeterminate }) => (
					<>
						<div className={styles.checkbox}>
							<CheckboxIcon isIndeterminate={isIndeterminate} customStyles={styles.svg} />
						</div>
						{(children || label) && (
							<label
								className={[styles.label, disabled && styles['label-disabled']].join(' ')}
								htmlFor={internalId}
							>
								{required && <abbr className={styles.required}>*</abbr>}
								{children ? children : label}
							</label>
						)}
					</>
				)}
			</Checkbox>
		</div>
	);
};
